import { Editor, EditorContent } from "@tiptap/react";
import {
    useContext,
    useEffect,
    useMemo,
    useState,
    useCallback,
    CSSProperties,
} from "react";
import { EditorBubbleMenu } from "@/components/EditorComponents/EditorBubbleMenu";
import { FieldIcon } from "@/components/FieldIcon/FieldIcon";
import {
    getTextColourStyle,
    getTextStyle,
} from "@/components/PdfDocument/utils/getStyles";
import { GLOBAL_STYLE_KEY } from "@/constants/resume";
import { EditorContext } from "@/context/EditorContext";
import { useAppSelector } from "@/hooks/types";
import { decodeHtml } from "@/pages/ResumeBuilder/utils";
import { getActiveSection } from "@/store/template/selectors";
import { EditorContextType } from "@/types/editor";
import { ResumeStyles } from "@/types/resume";

interface BodyTextProps {
    index?: number;
    id: string;
    value?: string;
    sectionId: string;
    itemId?: string;
    propType: "header" | "body";
    onClick?: (ids: { sectionId: string; itemId?: string }) => void;
    icon?: string;
    placeholder?: string;
    stylesConfig: ResumeStyles;
    isStaticHeader?: boolean;
}

const aiSupportedFieldIds = [
    "description",
    "summary",
    "skill",
    "accomplishmentdescription",
    "strength",
];

const isAiSupportedFieldId = (id: string) => {
    const canUseAi = aiSupportedFieldIds.includes(id?.toLocaleLowerCase());
    return canUseAi;
};

export const BodyText = ({
    id,
    value,
    sectionId,
    itemId,
    propType,
    onClick,
    icon,
    placeholder,
    stylesConfig,
    isStaticHeader,
}: BodyTextProps) => {
    const { setActiveEditor, addEditor, getEditor } = useContext(
        EditorContext,
    ) as EditorContextType;
    const editorId = useMemo(
        () => `${sectionId}-${id}-${itemId}`,
        [sectionId, id, itemId],
    );
    const [localEditor, setLocalEditor] = useState<Editor | null>(
        getEditor(editorId) || null,
    );
    const activeSection = useAppSelector(getActiveSection);

    const initializeEditor = useCallback(() => {
        const currEditor = getEditor(editorId);
        if (!currEditor) {
            const decodedValue = decodeHtml(value ?? "");
            const newEditor = addEditor(
                editorId,
                decodedValue,
                itemId,
                propType,
                sectionId,
                id,
                placeholder,
                undefined,
                icon,
                isStaticHeader,
            );

            setLocalEditor(newEditor);
        } else {
            setLocalEditor(currEditor);
        }
    }, [
        addEditor,
        editorId,
        getEditor,
        id,
        icon,
        isStaticHeader,
        itemId,
        placeholder,
        propType,
        sectionId,
        value,
    ]);

    useEffect(() => {
        initializeEditor();
    }, [initializeEditor]);

    const textStyle: CSSProperties = useMemo(
        () =>
            getTextStyle(
                stylesConfig?.[GLOBAL_STYLE_KEY]?.fontSize ?? "",
                "p",
                false,
            ),
        [stylesConfig],
    );

    const textColourStyle = getTextColourStyle({
        accentColor: stylesConfig?.[GLOBAL_STYLE_KEY]?.themeColor ?? "",
        isStaticHeader,
        fieldKey: id,
    });

    if (icon) {
        textStyle.display = "flex";
        textStyle.alignItems = "flex-start";
    }

    const onFocus = useCallback(() => {
        setActiveEditor(editorId);
        onClick?.({ sectionId, itemId });
    }, [setActiveEditor, editorId, onClick, sectionId, itemId]);

    const canUseAi = useMemo(() => {
        return isAiSupportedFieldId(id) && !!activeSection;
    }, [id, activeSection]);

    return (
        <div
            style={{
                wordBreak: "break-word",
                ...textStyle,
                ...textColourStyle,
            }}
        >
            {icon && (
                <FieldIcon
                    name={icon}
                    stylesConfig={stylesConfig}
                    isStaticHeader={!!isStaticHeader}
                />
            )}
            <EditorContent
                editor={localEditor}
                onFocus={onFocus}
            />
            {canUseAi && <EditorBubbleMenu editor={localEditor} />}
        </div>
    );
};
